import { useState, useEffect } from "react"
import { getAccessToken } from "../../auth"
import { post, getUserTransactions, downloadInvoice } from "../../api"
import { trackBillingPackageClick } from "../../utils/analytics"
import { Disclosure } from "@headlessui/react"
import { ChevronUpIcon } from "@heroicons/react/20/solid"
import { ShieldCheckIcon, LockClosedIcon, ArrowPathIcon, CreditCardIcon, CheckIcon } from "@heroicons/react/24/outline"
import { Button } from '../ui';

const creditPackages = window.location.hostname !== 'localhost' ? [
  { id: 1, credits: 100, price: 9.75, popular: false, stripe_payment_link: 'https://buy.stripe.com/14AbJ0dlS6IXetk3YXgYU0y' },
  { id: 2, credits: 250, price: 18.99, popular: true, stripe_payment_link: 'https://buy.stripe.com/4gMcN45Tq1oD3OGdzxgYU0w' },
  { id: 3, credits: 500, price: 34.49, popular: false, stripe_payment_link: 'https://buy.stripe.com/5kQ5kCepWgjx0CuanlgYU0x' },
] : [
  { id: 1, credits: 100, price: 9.75, popular: false, stripe_payment_link: 'https://buy.stripe.com/14AbJ0dlS6IXetk3YXgYU0y' },
  { id: 2, credits: 250, price: 18.99, popular: true, stripe_payment_link: 'https://buy.stripe.com/4gMcN45Tq1oD3OGdzxgYU0w' },
  { id: 3, credits: 500, price: 34.49, popular: false, stripe_payment_link: 'https://buy.stripe.com/5kQ5kCepWgjx0CuanlgYU0x' },
]

// Package descriptions (by index)
const packageDescriptions = [
  "Perfect for trying out the service",
  "Best for regular users",
  "For teams and heavy users",
]

// Stats to display under the hero
const stats = [
  { label: "Users", value: "5,500+" },
  { label: "Models compressed", value: "50,000+" },
  { label: "Data saved", value: "> 1 TB" },
  { label: "Uptime", value: "99.9%" },
]

// "Purchase with confidence" items
const confidence = [
  {
    icon: ShieldCheckIcon,
    title: "30-day money-back guarantee",
    desc: "Not satisfied? Get a full refund, no questions asked.",
  },
  {
    icon: LockClosedIcon,
    title: "Secure payment by Stripe",
    desc: "Your payment information is encrypted and secure.",
  },
  {
    icon: ArrowPathIcon,
    title: "Credits never expire",
    desc: "Use your credits whenever you need them, no time pressure.",
  },
  {
    icon: CreditCardIcon,
    title: "Cancel anytime",
    desc: "No subscriptions or commitments. Pay as you go.",
  },
]

// Simple mapping from locale to currency
function getCurrencyFromLocale(locale) {
  if (!locale) return 'EUR';
  const map = {
    'en-US': 'USD',
    'en-GB': 'GBP',
    'ja-JP': 'JPY',
    'en-AU': 'AUD',
    'en-CA': 'CAD',
    'fr-CH': 'CHF',
    'zh-CN': 'CNY',
    'hi-IN': 'INR',
    'de-DE': 'EUR',
    'fr-FR': 'EUR',
    'es-ES': 'EUR',
    'it-IT': 'EUR',
    'nl-NL': 'EUR',
    'bn-BD': 'BDT', // Bengali (Bangladesh)
    'ar-EG': 'EGP', // Arabic (Egypt)
    'id-ID': 'IDR', // Indonesian (Indonesia)
    'si-LK': 'LKR', // Sinhala (Sri Lanka)
    'ur-PK': 'PKR', // Urdu (Pakistan)
    'ru-RU': 'RUB', // Russian (Russia)
  }
  return map[locale] || 'EUR';
}

// Pretty rounding for all currencies
function prettyRound(amount) {
  if (amount < 10) return Math.round(amount * 2) / 2;
  amount = Math.round(amount);
  if (amount < 100) return Math.round(amount / 5) * 5;
  if (amount < 1000) return Math.round(amount / 10) * 10;
  if (amount < 10000) return Math.round(amount / 50) * 50;
  if (amount < 100000) return Math.round(amount / 100) * 100;
  return Math.round(amount / 1000) * 1000;
}

// Map of currencies where the symbol goes before the number
const symbolBefore = new Set([
  'USD', 'CAD', 'AUD', 'GBP', 'JPY', 'RUB', 'CHF', 'EGP', 'IDR',
]);

// Currencies for which we do NOT round (show exact cents)
const noRoundCurrencies = new Set(['USD', 'EUR', 'GBP']);

function formatPrice(amount, symbol, currency) {
  if (noRoundCurrencies.has(currency)) {
    const fixed = Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return symbolBefore.has(currency) ? `${symbol}${fixed}` : `${fixed} ${symbol}`;
  }
  const pretty = prettyRound(amount).toLocaleString();
  return symbolBefore.has(currency) ? `${symbol}${pretty}` : `${pretty} ${symbol}`;
}

function formatPerCredit(amount, symbol, currency) {
  if (noRoundCurrencies.has(currency)) {
    const fixed = Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return symbolBefore.has(currency) ? `${symbol}${fixed}` : `${fixed} ${symbol}`;
  }
  if (amount < 1) {
    return `${amount.toFixed(2)} ${symbol}`;
  }
  const rounded = Math.round(amount).toLocaleString();
  return symbolBefore.has(currency) ? `${symbol}${rounded}` : `${rounded} ${symbol}`;
}

export default function Billing() {
  const [localized, setLocalized] = useState(null)
  const [transactions, setTransactions] = useState([])

  useEffect(() => {
    const fetchLocalized = async () => {
      const locale = navigator.language || 'en-US';
      const currency = getCurrencyFromLocale(locale);
      const prices = creditPackages.map(pkg => pkg.price);
      try {
        const data = await post('/api/convert-prices', { prices, currency });
        if (data && data.prices && data.currency) {
          // Always apply "nines" pricing for the 100 credits package
          const supportedCurrencies = ['USD', 'GBP', 'EUR'];
          if (supportedCurrencies.includes(data.currency)) {
            data.prices[0] = 9.99;
          } else {
            // For other currencies, force USD display
            data.prices[0] = 9.99;
            data.currency = 'USD';
            data.symbol = '$';
          }

          setLocalized({
            prices: data.prices,
            currency: data.currency,
            symbol: data.symbol || data.currency,
            rate: data.rate
          });
        }
      } catch (e) {
        setLocalized(null);
      }
    };
    fetchLocalized();
    // Also load transactions for the read-only history section
    const loadTransactions = async () => {
      try {
        const tx = await getUserTransactions();
        setTransactions(Array.isArray(tx) ? tx : []);
      } catch (e) {
        setTransactions([]);
      }
    };
    loadTransactions();
  }, []);

  const handlePurchase = async (pkg, localizedPrice) => {
    // Track click before opening Stripe
    trackBillingPackageClick({
      price: localizedPrice ?? pkg.price,
      credits: pkg.credits,
      package_name: `${pkg.credits}_credits`
    });
    const accessToken = await getAccessToken()
    const email = accessToken.sub;
    const userId = accessToken.id;

    // Always use the "nines" payment link for 100 credits
    let paymentLink = pkg.stripe_payment_link;
    if (pkg.credits === 100) {
      paymentLink = 'https://buy.stripe.com/4gMdR84Pm0kzclcgLJgYU0z';
    }

    const params = new URLSearchParams({
      prefilled_email: email,
      client_reference_id: userId,
    }).toString()
    window.open(paymentLink + `?${params}`, '_blank')
  }

  return (
    <div className="space-y-16">
      {/* Hero section - conversion focused */}
      <div className="text-center max-w-4xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-extrabold text-foreground tracking-tight mb-4">
          Compress more. <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-primary">Pay less.</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Buy credits once, use them forever. No subscriptions, no recurring charges, no surprises.
        </p>
      </div>

      {/* Stats */}
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-background-elevated border border-border/50 rounded-xl p-5 text-center hover:border-border transition-all duration-200">
            <div className="text-3xl font-bold text-foreground mb-1">{s.value}</div>
            <div className="text-sm text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Pricing cards */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-foreground text-center mb-8">Choose your package</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {creditPackages.map((pkg, i) => (
            <div
              key={pkg.id}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${pkg.popular
                ? 'bg-gradient-to-br from-accent/10 to-primary/10 border-2 border-accent shadow-2xl shadow-accent/20 scale-105'
                : 'bg-background-elevated border border-border/50 hover:border-border hover:shadow-xl'
                }`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-accent to-primary text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                    22% off
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-3xl font-bold text-foreground mb-2">{pkg.credits} Credits</h3>
                <p className="text-sm text-muted-foreground">{packageDescriptions[i]}</p>
              </div>

              {localized && (
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-foreground">
                      {formatPrice(localized.prices[i], localized.symbol, localized.currency)}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm mt-1">
                    {formatPerCredit(localized.prices[i] / pkg.credits, localized.symbol, localized.currency)} per credit
                  </p>
                </div>
              )}

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <CheckIcon className="w-5 h-5 text-success flex-shrink-0" />
                  No expiration date
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <CheckIcon className="w-5 h-5 text-success flex-shrink-0" />
                  Use for files up to 500MB
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <CheckIcon className="w-5 h-5 text-success flex-shrink-0" />
                  Access to premium support
                </li>
              </ul>

              <Button
                variant={pkg.popular ? "accent" : "primary"}
                className="w-full"
                onClick={() => handlePurchase(pkg, localized ? localized.prices[i] : undefined)}
              >
                Purchase now
              </Button>
            </div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            Need more?{" "}
            <a href="mailto:stan@compress-glb.com" className="text-accent hover:text-accent-hover font-medium underline-offset-4 hover:underline">
              Contact me for enterprise pricing
            </a>
          </p>
        </div>
      </div>

      {/* Purchase with confidence */}
      <div className="max-w-6xl mx-auto">
        <h3 className="text-3xl font-bold text-foreground text-center mb-10">Purchase with confidence</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {confidence.map((c) => (
            <div key={c.title} className="flex flex-col items-center text-center gap-4 p-6 bg-background-elevated border border-border/50 rounded-xl hover:border-border transition-all duration-200">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <c.icon className="w-6 h-6 text-accent" />
              </div>
              <div>
                <div className="font-semibold text-foreground mb-1">{c.title}</div>
                <div className="text-sm text-muted-foreground">{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Objection-handling Billing FAQ */}
      <div className="max-w-4xl mx-auto" id="billing-faq">
        <h3 className="text-3xl font-bold text-foreground text-center mb-4">Frequently asked questions</h3>
        <p className="text-center text-muted-foreground mb-10 text-lg">Everything you need to know about our pricing and credits</p>
        <div className="bg-background-elevated rounded-2xl border border-border/50 overflow-hidden shadow-xl">
          {(() => {
            const items = [
              {
                q: "How do credits work?",
                a: (
                  <>
                    Every successful compression job costs 1 credit. Credits never expire and you can use them anytime from your dashboard. Larger credit packs give you a better per‑credit price, but you’re never locked into a subscription.
                  </>
                ),
              },
              {
                q: "Can I upgrade or downgrade my plan?",
                a: (
                  <>
                    There’s no subscription to manage. Simply purchase a different credit pack whenever you need more (or less). For high‑volume teams, <a href="mailto:stan@compress-glb.com" className="text-primary hover:underline">contact me</a> for enterprise options.
                  </>
                ),
              },
              {
                q: "What file formats do you support?",
                a: (
                  <>
                    <strong className="text-foreground">.glb</strong> and <strong className="text-foreground">.gltf</strong>. If your .gltf references external textures, make sure they’re included alongside the file before uploading.
                  </>
                ),
              },
              {
                q: "Is there a file size limit?",
                a: (
                  <>
                    The web interface supports files up to 500&nbsp;MB. Anonymous uploads are limited to 10&nbsp;MB. Need larger files? Use the API or <a href="mailto:stan@compress-glb.com" className="text-primary hover:underline">reach out</a> and I’ll help.
                  </>
                ),
              },
              {
                q: "What’s your refund policy?",
                a: (
                  <>
                    A 30‑day money‑back guarantee applies to all purchases. If you’re not satisfied, reply to your receipt or email <a href="mailto:stan@compress-glb.com" className="text-primary hover:underline">stan@compress-glb.com</a> for a full refund.
                  </>
                ),
              },
              {
                q: "Do you offer discounts for bulk purchases?",
                a: (
                  <>
                    Yes. I offer volume discounts and custom invoicing for studios and teams. <a href="mailto:stan@compress-glb.com" className="text-primary hover:underline">Contact me</a> and include your estimated monthly volume.
                  </>
                ),
              },
              {
                q: "Who am I doing business with?",
                a: (
                  <>
                    Hi, I’m Stan — <a href="https://x.com/stanvanrooy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">@stanvanrooy</a>. I’m a hobby game developer who built Compress&nbsp;GLB for my own projects, then shared it because others found it useful. It’s now the most popular GLB compression service online.
                  </>
                ),
              },
            ];
            return items.map((faq, index) => (
              <Disclosure key={faq.q}>
                {({ open }) => (
                  <div className={`${index === 0 ? "rounded-t-lg" : ""} ${index === items.length - 1 ? "rounded-b-lg" : ""} overflow-hidden border-t border-border`}>
                    <Disclosure.Button className="flex justify-between items-center w-full px-6 py-4 text-left text-lg font-medium text-foreground bg-transparent hover:bg-background-elevated transition duration-200">
                      <span>{faq.q}</span>
                      <ChevronUpIcon className={`${open ? "transform rotate-180" : ""} w-5 h-5 text-primary`} />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground bg-transparent">{faq.a}</Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            ));
          })()}
        </div>
      </div>

      {/* Transactions History */}
      <div className="max-w-6xl mx-auto">
        <h3 className="text-2xl font-bold text-foreground mb-4">Payment history</h3>
        <div className="bg-background-elevated border border-border/50 rounded-xl overflow-hidden">
          {transactions.length === 0 ? (
            <div className="p-6 text-muted-foreground text-sm">No payments found yet.</div>
          ) : (
            <div className="divide-y divide-border/50">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground">
                <div className="col-span-4 md:col-span-4">Date</div>
                <div className="col-span-2 md:col-span-2">Credits</div>
                <div className="col-span-3 md:col-span-3">Amount (EUR)</div>
                <div className="col-span-2 md:col-span-2">Status</div>
                <div className="col-span-1 md:col-span-1 text-right">Invoice</div>
              </div>
              {transactions.map((t) => (
                <div key={t.id} className="grid grid-cols-12 gap-4 px-4 py-3 text-sm items-center">
                  <div className="col-span-4 md:col-span-4 text-foreground">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</div>
                  <div className="col-span-2 md:col-span-2 text-foreground">{t.credits ?? '-'}</div>
                  <div className="col-span-3 md:col-span-3 text-foreground">{typeof t.amount === 'number' ? t.amount.toFixed(2) : '-'}</div>
                  <div className="col-span-2 md:col-span-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${t.status === 'completed' ? 'bg-success/10 text-success border-success/30' :
                      t.status?.startsWith('dispute') ? 'bg-warning/10 text-warning border-warning/30' :
                        'bg-muted/10 text-muted-foreground border-border/40'
                      }`}>
                      {t.status || '—'}
                    </span>
                  </div>
                  <div className="col-span-1 md:col-span-1 text-right">
                    <button
                      className="text-primary hover:underline disabled:opacity-50"
                      onClick={async () => {
                        try {
                          const blob = await downloadInvoice(t.id);
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const datePart = t.created_at ? new Date(t.created_at).toISOString().split('T')[0] : 'invoice';
                          a.download = `invoice_${datePart}_${t.id.slice(0, 8)}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Failed to download invoice.');
                        }
                      }}
                      disabled={!t.id}
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
